import { RouterV2Enum } from '@/router/router-names';
import VueRouter from 'vue-router';
import wechat from '@/api/native/wechat';
import { triggerCashActivity } from '@/api/rewarding';
import { creditMallApply } from '@/api/rights/shop-mall';
import { applyMiddleIncomeCredit } from '@/api/middle-income';
import type { CreditMallFromEnum } from '@/api/middle-income/types';
import { closeWebView, nativeLocalSetData } from '@/api/native/utils';
import isSupportNativeDataStorage from '../utils/isSupportNativeDataStorage';
import { ubtSendError } from '../utils/ubt';
import platform from '../utils/platform';
import { ResolveVerifyStageEnum } from './types';
import { jumpToPage } from './utils';

/** 跳转到授信分发页 */
export function jumpToCreditProcessingPage(this: VueRouter, query: Dictionary<string>): void {
  jumpToPage.call(this, {
    path: RouterV2Enum.preApplyChoose,
    query,
  }, true);
}

/** 跳转到授信结果页 */
export function jumpToCreditResultPage(this: VueRouter, query: Dictionary<string>): void {
  jumpToPage.call(this, {
    path: RouterV2Enum.preApplyResult,
    query: {
      taskId: query.creditId,
    },
  });
}

/** 跳转到提现申请页 */
export function jumpToApplyProductPage(this: VueRouter, query: Dictionary<string>): void {
  jumpToPage.call(this, {
    path: RouterV2Enum.applyProduct,
    query,
  });
}

/** 回跳到首页 */
export function jumpToHomeV2Page(this: VueRouter, query: Dictionary<string>): void {
  jumpToPage.call(this, {
    path: RouterV2Enum.home,
    query,
  });
}

/** 跳转到提现结果页 */
export function jumpToApplyResultPage(this: VueRouter, query: Dictionary<string>): void {
  jumpToPage.call(this, {
    path: RouterV2Enum.applyResult,
    query,
  });
}

/** back返回上一页面 */
export function backToPreviousPage(this: VueRouter): void {
  if (platform.isWechatMini && window.history.length === 1) {
    wechat.navigateBack();
  } else {
    this.back();
  }
}

/** 跳转到现金活动结果页 */
export async function jumpToCashActivityResultPage(
  this: VueRouter,
  query: Dictionary<string>,
  stage?: ResolveVerifyStageEnum,
): Promise<void> {
  /**
   * 在操作完成阶段，触发现金活动奖励
   * 目前前端暂不处理业务异常情况，后端添加捕获和埋点
   */
  if (stage === ResolveVerifyStageEnum.OPERATION_COMPLETE) {
    await triggerCashActivity();
  }
  jumpToPage.call(this, {
    path: stage === ResolveVerifyStageEnum.INIT
      ? RouterV2Enum.cashActivityDisqualification
      : RouterV2Enum.cashActivitySuccess,
    query,
  }, true);
}

// 调用权益同步接口，跳转分期商城购买页
export async function shopMallCallback(this: VueRouter): Promise<void> {
  /** 开额接口 */
  const { source, productIds, callBackUrl } = this.currentRoute?.query;
  try {
    if (productIds && productIds?.split(',').length) {
      const response = await creditMallApply({
        from: source,
        productIds: productIds?.split(',') || [],
      });
      // 修复点：安全访问属性 + 空值检查
      if (response?.creditTaskId) {
        if (callBackUrl) {
          const url = decodeURIComponent(callBackUrl);

          // 构建查询参数（只拼接非空值）
          const params: string[] = [];
          if (response.creditTaskId) {
            params.push(`creditTaskId=${String(response.creditTaskId)}`);
          }

          // 判断 URL 是否已有查询参数
          const separator = url.includes('?') ? '&' : '?';
          const finalUrl = `${url}${separator}${params.join('&')}`;

          window.location.replace(finalUrl);
        } else {
          // 如果没有带callback，则返回上一页
          // 目前场景只有维雅渠道跳转我们的资料页不会带callback
          window.history.go(-1);
        }
      } else {
        ubtSendError(new Error('creditMallApply 返回无效数据'));
      }
    } else {
      ubtSendError('productIds is empty');
    }
  } catch (error) {
    ubtSendError('creditMallApply error');
  }
}

export async function shopMallCallback2(this: VueRouter): Promise<void> {
  const {
    from, productIds,
  } = this.currentRoute?.query;
  try {
    // 如果客户端不支持nativeLocalSetData，则直接关闭webview
    if (!isSupportNativeDataStorage) {
      closeWebView();
      return;
    }
    if (productIds && productIds?.split(',').length) {
      const response = await applyMiddleIncomeCredit({
        from: from as CreditMallFromEnum,
        shoppingMallCommodityList: productIds?.split(',') || [],
        priorityLevel: ['shoppingMall'],
      });
      const { taskId } = response || {};
      // 安全访问属性 + 空值检查
      if (taskId) {
        nativeLocalSetData({
          key: 'AppFinanceH5_middleIncome_fqtTaskId',
          value: [taskId],
        });
      }
    } else {
      ubtSendError('productIds is empty');
    }
    closeWebView();
  } catch (error) {
    closeWebView();
    ubtSendError('creditMallApply error');
  }
}
